import { ref, computed, watch, unref } from '@vue/composition-api'
import drawsApi from '@/services/api/draws'
import calculatorsApi from '@/services/api/calculators'
import EventBus from '@/store/EventBus'

export default function useDrawsEventView(eventIdInput, draftIdInput, initialRound) {
  const eventId = computed(() => unref(eventIdInput))
  const draftId = computed(() => unref(draftIdInput))
  const meta = ref(null)
  const draws = ref([])
  const nextCursor = ref(null)
  const hasMore = ref(false)
  const loading = ref(false)
  const loadingMore = ref(false)
  const emptySlots = ref([])
  const swapCandidates = ref([])
  const swapCandidatesLoading = ref(false)
  const eventInfo = ref(null)
  const redirectTo = ref(null)
  const activeRound = ref(initialRound != null ? Number(initialRound) : 1)

  const draft = computed(() => (meta.value != null ? meta.value.draft : null))
  const eventName = computed(() => (eventInfo.value != null && eventInfo.value.event != null ? eventInfo.value.event.name : ''))
  const judgesNum = computed(() => (eventInfo.value != null && eventInfo.value.event != null ? eventInfo.value.event.judgesNum : 1))
  const autoSendTexts = computed(() => (draft.value != null ? draft.value.auto_send_texts : false))

  const rounds = computed(() => (meta.value != null && meta.value.rounds != null ? meta.value.rounds : []))
  const eventRoundsNum = computed(() => (meta.value != null ? meta.value.eventRoundsNum : 0))
  const lastRoundIndex = computed(() => (meta.value != null ? meta.value.lastRoundIndex : 0))
  const calculatorId = computed(() => (meta.value != null ? meta.value.calculatorId : null))
  const calculator = computed(() => (meta.value != null ? meta.value.calculator : null))
  const finalRoundExists = computed(() => (meta.value != null ? meta.value.finalRoundExists : false))
  const isParentSplit = computed(() => (meta.value != null ? meta.value.isParentSplit : false))
  const isBrotherCombSplit = computed(() => (meta.value != null ? meta.value.isBrotherCombSplit : false))
  const parentEventId = computed(() => (meta.value != null ? meta.value.parentEventId : null))
  const childEventId = computed(() => (meta.value != null ? meta.value.childEventId : null))
  const childrenEventListWithParent = computed(() => (meta.value != null && meta.value.childrenEventListWithParent != null ? meta.value.childrenEventListWithParent : []))
  const finalEvent = computed(() => (meta.value != null ? meta.value.finalEvent : null))
  const allSiblingsHaveDraws = computed(() => (meta.value != null ? meta.value.allSiblingsHaveDraws : true))
  const existScratchedDraw = computed(() => (meta.value != null ? meta.value.existScratchedDraw : false))
  const draftEvents = computed(() => (meta.value != null && meta.value.draftEvents != null ? meta.value.draftEvents : []))
  const refundQueueNotEmpty = ref(false)
  const paymentMethods = computed(() => (meta.value != null && meta.value.paymentMethods != null ? meta.value.paymentMethods : []))
  const disabledPaymentMethods = computed(() => (meta.value != null && meta.value.disabledPaymentMethods != null ? meta.value.disabledPaymentMethods : []))
  const scoreLimits = computed(() => (meta.value != null && meta.value.scoreLimits != null ? meta.value.scoreLimits : []))

  const roundMeta = computed(() => (meta.value != null ? meta.value.roundMeta : null))
  const emptyDrawsOrders = computed(() => (roundMeta.value != null && roundMeta.value.emptyDrawsOrders != null ? roundMeta.value.emptyDrawsOrders : []))
  const unpublishedDrawsCount = computed(() => (roundMeta.value != null ? roundMeta.value.unpublishedDrawsCount : 0))
  const showNotificationsModal = computed(() => (roundMeta.value != null ? roundMeta.value.showNotificationsModal : false))
  const isNextRoundGenerated = computed(() => (roundMeta.value != null ? roundMeta.value.isNextRoundGenerated : false))
  const showRunOffPanel = computed(() => (roundMeta.value != null ? roundMeta.value.showRunOffPanel : false))

  // Starts from the value loaded with roundMeta, then kept fresh after each score
  // save via refreshTieCheck() — a full eventDrawsMeta refetch is too heavy to run
  // after every mark, so this hits a dedicated lightweight endpoint instead.
  const hasTiedWinners = ref(false)
  watch(roundMeta, (val) => {
    hasTiedWinners.value = val != null ? Boolean(val.hasTiedWinners) : false
  })

  async function refreshTieCheck(round) {
    const r = round != null ? round : activeRound.value
    try {
      const res = await drawsApi.getTieCheck(eventId.value, r)
      hasTiedWinners.value = Boolean(res.data.data.hasTiedWinners)
    } catch (_) {
      // keep last known value
    }
  }

  async function fetchInfo() {
    try {
      const res = await drawsApi.getEventInfo(eventId.value)
      eventInfo.value = res.data.data
      return eventInfo.value
    } catch (_) {
      eventInfo.value = null
      return null
    }
  }

  async function fetchRoundCheck(round) {
    try {
      const res = await drawsApi.getRoundCheck(eventId.value, round)
      return res.data.data
    } catch (_) {
      return null
    }
  }

  async function fetchMeta(round) {
    const r = round != null ? round : activeRound.value
    try {
      const res = await drawsApi.getEventMeta(eventId.value, { round: r })
      meta.value = res.data.data
    } catch (_) {
      meta.value = null
    }
  }

  async function fetchDraws(round, params) {
    try {
      const r = round != null ? round : activeRound.value
      const query = Object.assign({ sort: 'order', direction: 'asc' }, params)
      const res = await drawsApi.getRoundDraws(eventId.value, r, query)
      draws.value = res.data.data
      const resMeta = res.data.meta
      nextCursor.value = resMeta != null ? resMeta.next_cursor : null
      hasMore.value = resMeta != null ? resMeta.has_more : false
    } catch (_) {
      draws.value = []
      nextCursor.value = null
      hasMore.value = false
    }
  }

  async function fetchEmptySlots(round) {
    const r = round != null ? round : activeRound.value
    try {
      const res = await drawsApi.getEmptySlots(eventId.value, r)
      emptySlots.value = res.data.data
    } catch (_) {
      emptySlots.value = []
    }
  }

  async function fetchSwapCandidates() {
    swapCandidatesLoading.value = true
    try {
      const res = await drawsApi.swapCandidates(eventId.value, activeRound.value)
      swapCandidates.value = res.data.data
    } catch (_) {
      swapCandidates.value = []
    } finally {
      swapCandidatesLoading.value = false
    }
  }

  async function loadMore() {
    if (!hasMore.value || nextCursor.value == null || loadingMore.value) return
    loadingMore.value = true
    try {
      const res = await drawsApi.getRoundDraws(eventId.value, activeRound.value, {
        sort: 'order',
        direction: 'asc',
        cursor: nextCursor.value
      })
      draws.value = draws.value.concat(res.data.data)
      const resMeta = res.data.meta
      nextCursor.value = resMeta != null ? resMeta.next_cursor : null
      hasMore.value = resMeta != null ? resMeta.has_more : false
    } catch (_) {
      // keep existing draws
    } finally {
      loadingMore.value = false
    }
  }

  let infoLoaded = false

  watch(eventId, () => { infoLoaded = false })

  async function fetchAll(round) {
    loading.value = true
    redirectTo.value = null
    const r = round != null ? round : activeRound.value

    try {
      // Step 1: event info — only on first load
      if (!infoLoaded) {
        const info = await fetchInfo()
        if (info == null || !info.hasDraws) {
          redirectTo.value = { path: '/draws/dashboard', message: 'Round is not generated yet.' }
          return
        }
        infoLoaded = true
      }

      // Step 2: round check — does this round exist?
      const check = await fetchRoundCheck(r)
      if (check == null || !check.roundExists) {
        const firstRound = check != null ? check.firstAvailableRound : null
        if (firstRound != null && firstRound !== r) {
          redirectTo.value = { round: firstRound, message: 'Round is not generated yet. Redirected to the first available round.' }
        } else {
          redirectTo.value = { path: '/draws/dashboard', message: 'Round is not generated yet.' }
        }
        return
      }

      // Step 3: load meta + draws + empty slots in parallel
      await Promise.all([fetchMeta(r), fetchDraws(r), fetchEmptySlots(r)])
    } finally {
      loading.value = false
    }
  }

  async function refreshDraws() {
    await fetchDraws(activeRound.value)
  }

  async function refreshDrawsAndMeta() {
    await Promise.all([
      fetchMeta(activeRound.value),
      fetchDraws(activeRound.value),
      fetchEmptySlots(activeRound.value)
    ])
  }

  function setRound(index) {
    activeRound.value = index
  }

  function patchDraws(affected, preserveFields) {
    if (!Array.isArray(affected) || affected.length === 0) return
    const list = draws.value
    affected.forEach((item) => {
      const idx = list.findIndex(d => d.id === item.id)
      if (idx !== -1) {
        if (preserveFields != null && preserveFields[item.id] != null) {
          const fields = preserveFields[item.id]
          const merged = Object.assign({}, item)
          fields.forEach((f) => { merged[f] = list[idx][f] })
          list.splice(idx, 1, merged)
        } else {
          list.splice(idx, 1, item)
        }
      } else {
        const maxOrder = list.reduce((max, d) => (d.order > max ? d.order : max), -1)
        if (!hasMore.value || item.order <= maxOrder) {
          list.push(item)
        }
      }
    })
  }

  function extractError(err) {
    const data = err.response != null ? err.response.data : null
    return data != null && data.message != null ? data.message : 'Something went wrong.'
  }

  async function markDraw(drawId, data, preserveFields) {
    try {
      const res = await drawsApi.markDraw(drawId, data)
      patchDraws(res.data.data, preserveFields)
      return { message: res.data.message || null, type: 'success' }
    } catch (err) {
      return { message: extractError(err), type: 'warning' }
    }
  }

  async function deleteDraw(drawId) {
    try {
      const res = await drawsApi.deleteDraw(drawId)
      patchDraws(res.data.data)
      fetchEmptySlots()
      return { message: res.data.message || null, type: 'success' }
    } catch (err) {
      return { message: extractError(err), type: 'warning' }
    }
  }

  async function moveDraw(drawId, data) {
    try {
      const res = await drawsApi.moveDraw(drawId, data)
      patchDraws(res.data.data)
      fetchEmptySlots()
      return { message: res.data.message || null, type: 'success' }
    } catch (err) {
      return { message: extractError(err), type: 'warning' }
    }
  }

  async function swapDraws(data) {
    try {
      const res = await drawsApi.swapDraws(data)
      patchDraws(res.data.data)
      return { message: res.data.message || null, type: 'success' }
    } catch (err) {
      return { message: extractError(err), type: 'warning' }
    }
  }

  async function fetchRefundStatus() {
    try {
      const res = await drawsApi.refundStatus(eventId.value)
      const data = res.data.data != null ? res.data.data : {}
      refundQueueNotEmpty.value = data.queueNotEmpty === true
    } catch (_) { /* handled by http interceptor */ }
  }

  async function refundDraws(refundType) {
    try {
      const res = await drawsApi.refund(eventId.value, { refund_type: refundType })
      await fetchRefundStatus()
      return { message: res.data.message || null, type: 'success' }
    } catch (err) {
      return { message: extractError(err), type: 'warning' }
    }
  }

  async function cleanSlate(drawIds) {
    try {
      const res = await drawsApi.cleanSlate({ draw_ids: drawIds })
      patchDraws(res.data.data)
      return { message: res.data.message || null, type: 'success' }
    } catch (err) {
      return { message: extractError(err), type: 'warning' }
    }
  }

  async function publishRound() {
    try {
      const res = await drawsApi.publishRound(eventId.value, activeRound.value)
      await refreshDrawsAndMeta()
      return { message: res.data.message || null, type: 'success' }
    } catch (err) {
      return { message: extractError(err), type: 'warning' }
    }
  }

  async function resetTotalScore() {
    try {
      const res = await drawsApi.resetTotalScore(eventId.value, activeRound.value)
      await refreshDrawsAndMeta()
      return { message: res.data.message || null, type: 'success' }
    } catch (err) {
      return { message: extractError(err), type: 'warning' }
    }
  }

  async function resendNotifications() {
    try {
      // members_id intentionally omitted: draws.value is only the currently-loaded page of a
      // cursor-paginated list (50/page), not every active draw in this round. Empty members_id
      // means "every competitor" per ResendDrawNotificationsRequest, same as the Committee
      // Dashboard's resend modal.
      const res = await drawsApi.resendNotifications(draftId.value, {
        events_id: [eventId.value],
        members_id: [],
        round: activeRound.value - 1
      })
      return { message: res.data.message || null, type: 'success' }
    } catch (err) {
      return { message: extractError(err), type: 'warning' }
    }
  }

  async function updateStatus(drawId, data) {
    try {
      const res = await drawsApi.updateStatus(drawId, data)
      patchDraws(res.data.data.draws)
      if (meta.value != null) meta.value.existScratchedDraw = res.data.data.existScratchedDraw
      fetchEmptySlots()
      return { message: res.data.message || null, type: 'success' }
    } catch (err) {
      return { message: extractError(err), type: 'warning' }
    }
  }

  async function regenerateFirstRound() {
    try {
      const res = await drawsApi.regenerateFirstRound(eventId.value)
      await refreshDrawsAndMeta()
      return { message: res.data.message || null, type: 'success' }
    } catch (err) {
      return { message: extractError(err), type: 'warning' }
    }
  }

  async function generateRound(round) {
    try {
      const res = await drawsApi.generateRound(eventId.value, round)
      if (res.data.message) EventBus.$emit('show-success', res.data.message)
      await refreshDrawsAndMeta()
    } catch (_) { /* http.js handles toast */ }
  }

  async function generateRunOff() {
    try {
      const res = await drawsApi.generateRunOff(eventId.value)
      const data = res.data.data
      return {
        message: res.data.message || null,
        type: 'success',
        round: data != null && data.round != null ? data.round : null
      }
    } catch (err) {
      return { message: extractError(err), type: 'warning' }
    }
  }

  async function insertDraw(data) {
    try {
      const res = await drawsApi.insert(eventId.value, data)
      const resData = res.data.data
      const deletedIds = resData.deleted_ids != null ? resData.deleted_ids : []
      if (deletedIds.length > 0) {
        draws.value = draws.value.filter(d => deletedIds.indexOf(d.id) === -1)
      }
      patchDraws(resData.draws)
      fetchEmptySlots()
      return { message: res.data.message || null, type: 'success' }
    } catch (err) {
      return { message: extractError(err), type: 'warning' }
    }
  }

  async function restoreDraw(drawId, data) {
    try {
      const res = await drawsApi.restore(drawId, data)
      await refreshDrawsAndMeta()
      return { message: res.data.message || null, type: 'success' }
    } catch (err) {
      return { message: extractError(err), type: 'warning' }
    }
  }

  async function updateCommit(nominationId, commit) {
    try {
      const res = await drawsApi.updateNominationCommit(draftId.value, {
        ids: [nominationId],
        commit
      })
      patchDraws(res.data.data)
      return { message: res.data.message || null, type: 'success' }
    } catch (err) {
      return { message: extractError(err), type: 'warning' }
    }
  }

  async function togglePaymentStatus(draw) {
    const nom = draw.nomination
    if (nom == null) return { message: null, type: 'warning' }
    if (nom.commit == null || nom.commit === '') {
      return { message: 'Please set up the payment type', type: 'warning' }
    }
    const newStatus = !nom.is_paid
    try {
      const res = await drawsApi.updateNominationPayment(draftId.value, {
        items: [{ id: nom.id, is_paid: newStatus ? 1 : 0 }]
      })
      patchDraws(res.data.data)
      return { message: res.data.message || null, type: 'success' }
    } catch (err) {
      return { message: extractError(err), type: 'warning' }
    }
  }

  async function reorderDraw(drawId, newOrder, notify = false) {
    const list = draws.value
    const idx = list.findIndex(d => d.id === drawId)
    if (idx === -1) return { message: null, type: 'warning' }

    const oldOrder = list[idx].order
    if (oldOrder === newOrder) return { message: null, type: 'warning' }

    const snapshot = list.map(d => ({ id: d.id, order: d.order }))
    const minO = Math.min(oldOrder, newOrder)
    const maxO = Math.max(oldOrder, newOrder)
    const shift = oldOrder < newOrder ? -1 : 1
    list.forEach((d, i) => {
      if (d.id === drawId) {
        list.splice(i, 1, Object.assign({}, d, { order: newOrder }))
      } else if (d.order >= minO && d.order <= maxO) {
        list.splice(i, 1, Object.assign({}, d, { order: d.order + shift }))
      }
    })

    try {
      const res = await drawsApi.reorder(eventId.value, activeRound.value, {
        draw_id_from: drawId,
        order: newOrder,
        notify
      })
      fetchEmptySlots()
      return { message: res.data.message || null, type: 'success' }
    } catch (err) {
      snapshot.forEach((s) => {
        const i = list.findIndex(d => d.id === s.id)
        if (i !== -1) list.splice(i, 1, Object.assign({}, list[i], { order: s.order }))
      })
      return { message: extractError(err), type: 'warning' }
    }
  }

  async function setWinner(drawId) {
    try {
      const res = await drawsApi.setWinner(drawId)
      patchDraws(res.data.data)
      return { message: res.data.message || null, type: 'success' }
    } catch (err) {
      return { message: extractError(err), type: 'warning' }
    }
  }

  async function createCalculator() {
    try {
      const res = await calculatorsApi.createCalculator(eventId.value, {})
      return { id: res.data.data.id, message: res.data.message || null, type: 'success' }
    } catch (err) {
      return { id: null, message: extractError(err), type: 'warning' }
    }
  }

  async function generateNext(score) {
    const fromEventId = (roundMeta.value && roundMeta.value.drawEventId) ? roundMeta.value.drawEventId : eventId.value
    const dbRound = activeRound.value - 1
    try {
      const res = await drawsApi.generateNext(fromEventId, { round: dbRound, count: score != null ? Number(score) : 0 })
      const data = res.data.data
      return {
        message: res.data.message || null,
        type: 'success',
        round: data != null && data.round != null ? data.round : null
      }
    } catch (err) {
      return { message: extractError(err), type: 'warning' }
    }
  }

  async function generateInto(toEventId, score) {
    const fromEventId = (roundMeta.value && roundMeta.value.drawEventId) ? roundMeta.value.drawEventId : eventId.value
    const dbRound = activeRound.value - 1
    try {
      const res = await drawsApi.generateInto(fromEventId, toEventId, { round: dbRound, count: score != null ? Number(score) : 0 })
      const data = res.data.data
      return {
        message: res.data.message || null,
        type: 'success',
        round: data != null && data.round != null ? data.round : null
      }
    } catch (err) {
      return { message: extractError(err), type: 'warning' }
    }
  }

  return {
    meta,
    draws,
    loading,
    loadingMore,
    hasMore,
    eventInfo,
    redirectTo,
    activeRound,
    draft,
    eventName,
    judgesNum,
    autoSendTexts,
    rounds,
    eventRoundsNum,
    lastRoundIndex,
    calculatorId,
    calculator,
    finalRoundExists,
    isParentSplit,
    isBrotherCombSplit,
    parentEventId,
    childEventId,
    childrenEventListWithParent,
    finalEvent,
    allSiblingsHaveDraws,
    existScratchedDraw,
    draftEvents,
    refundQueueNotEmpty,
    paymentMethods,
    disabledPaymentMethods,
    scoreLimits,
    roundMeta,
    emptyDrawsOrders,
    emptySlots,
    swapCandidates,
    swapCandidatesLoading,
    unpublishedDrawsCount,
    showNotificationsModal,
    isNextRoundGenerated,
    showRunOffPanel,
    hasTiedWinners,
    refreshTieCheck,
    fetchMeta,
    fetchDraws,
    loadMore,
    fetchAll,
    refreshDraws,
    refreshDrawsAndMeta,
    setRound,
    markDraw,
    deleteDraw,
    moveDraw,
    swapDraws,
    refundDraws,
    fetchRefundStatus,
    cleanSlate,
    publishRound,
    resetTotalScore,
    resendNotifications,
    updateStatus,
    regenerateFirstRound,
    generateRound,
    createCalculator,
    generateNext,
    generateInto,
    generateRunOff,
    insertDraw,
    restoreDraw,
    setWinner,
    reorderDraw,
    updateCommit,
    togglePaymentStatus,
    fetchSwapCandidates
  }
}



// WEBPACK FOOTER //
// ./src/composables/draws/useDrawsEventView.js